import { useState } from 'react';
import AcceptInviteForm from './AcceptInviteForm';
import AcceptInviteSuccess from './AcceptInviteSuccess';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './AcceptInviteModal.module.css';

/**
 * AcceptInviteModal Component
 *
 * Modal for accepting budget invites
 * - Enter invite code (with or without dash)
 * - Validates and accepts invite
 * - Shows success message with budget name
 *
 * Props:
 * - userEmail (string): Current user's email
 * - onClose (function): Called when modal is closed
 * - onSuccess (function): Called when invite is successfully accepted
 * - getAccessTokenSilently (function): Auth0 function to get access token
 */
function AcceptInviteModal({ _userEmail, onClose, onSuccess, getAccessTokenSilently }) {
    const [inviteCode, setInviteCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    // This modal is always "open" when rendered
    useModalScrollLock(true);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleAccept();
        }
    };

    const handleDone = () => {
        onSuccess();
    };

    const handleAccept = async () => {
        if (!inviteCode.trim()) {
            setError('Please enter an invite code');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/invites/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    inviteCode: inviteCode.trim()
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to accept invite');
            }

            setSuccess(data.budget.budgetName);
            // Automatically call onSuccess after a short delay to show the success message
            setTimeout(() => {
                onSuccess(data.budget);
            }, 1500);
        } catch (err) {
            // Error accepting invite
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <h2 className={styles.title}>Accept Budget Invite</h2>

                {!success ? (
                    <AcceptInviteForm
                        inviteCode={inviteCode}
                        setInviteCode={setInviteCode}
                        handleAccept={handleAccept}
                        loading={loading}
                        error={error}
                        onClose={onClose}
                        handleKeyPress={handleKeyPress}
                    />
                ) : (
                    <AcceptInviteSuccess
                        success={success}
                        handleDone={handleDone}
                    />
                )}
            </div>
        </div>
    );
}

export default AcceptInviteModal;
